import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import { ApiPairs, ApiProjects } from 'api';
import { IGetByIdProject } from 'api/apiProjects/models';
import { ICexPairShort, IDexPairShort } from 'types/pairs';
import { IProject, IProjectShort } from 'types/project';
import { EExchange, ExchangeRouteBasePath } from 'web3';

const fillProjectWithPairs = async (projects: IProjectShort[]) => {
  const newProjects: IProject[] = projects
    .sort((a, b) => {
      if (dayjs(a.created_at).valueOf() > dayjs(b.created_at).valueOf()) {
        return 1;
      }

      if (dayjs(a.created_at).valueOf() < dayjs(b.created_at).valueOf()) {
        return -1;
      }

      return 0;
    })
    .map(authProject => ({ ...authProject, dexPairs: [], cexPairs: [], solPairs: [] }));

  const [newProjectsUpdated] = await Promise.all([
    Promise.all(
      newProjects.map(async project => {
        const { isSuccess, data } = await ApiPairs.getPairsByProjectId({ id: project.id });

        if (!isSuccess) {
          return { ...project, pairs: [] };
        } else {
          const dexPairs = data.items
            .filter(el => el.type === EExchange.dex)
            .map(el => ({ ...el.dex_pair! }));
          const cexPairs = data.items
            .filter(el => el.type === EExchange.cex)
            .map(el => ({ ...el.cex_pair! }));
          const solPairs = data.items
            .filter(el => el.type === EExchange.sol)
            .map(el => ({ ...el.sol_pair! }));

          return {
            ...project,
            dexPairs,
            cexPairs,
            solPairs,
          };
        }
      }),
    ),
  ]);

  return newProjectsUpdated;
};

const isProjectArchived = ({
  active,
  projectId,
}: {
  active: IProject[] | null;
  projectId: number;
}) => {
  if (active && active.find(el => el.id === projectId)) return false;

  return true;
};

interface IProjectsSliceState {
  projects: null | IProject[];
  archivedProjects: null | IProject[];
  selectedProjectInPage: null | IGetByIdProject;
}

const userSliceInitialState: IProjectsSliceState = {
  projects: null,
  archivedProjects: null,
  selectedProjectInPage: null,
};

const getAllProjectsAndPairs = createAsyncThunk<
  any,
  {
    redirect: ({
      projectId,
      pairPath,
    }: {
      projectId: number | undefined;
      pairPath: string | undefined;
    }) => void;
  },
  any
>('projects/get-projects-n-pairs', async ({ redirect }, thunkApi) => {
  try {
    const { data, isSuccess, errorMessage } = await ApiProjects.getAllProjects({});

    if (isSuccess) {
      const [newProjects, newArchivedProjects] = await Promise.all([
        fillProjectWithPairs(data.items),
        fillProjectWithPairs(data.archived_items),
      ]);

      const pairs = newProjects[0]
        ? [
            ...(newProjects[0].dexPairs ?? []),
            ...(newProjects[0].cexPairs ?? []),
            ...(newProjects[0].solPairs ?? []),
          ].sort((a, b) => new Date(a.created_at).valueOf() - new Date(b.created_at).valueOf())
        : [];

      let pairPath;
      if (pairs.length > 0) {
        const firstPair = pairs[0];

        if ('cex' in firstPair) {
          pairPath = `${ExchangeRouteBasePath[EExchange.cex]}/${firstPair.id}`;
        } else if ('dex' in firstPair) {
          if (firstPair.network === 'solana') {
            pairPath = `${ExchangeRouteBasePath[EExchange.sol]}/${firstPair.id}`;
          } else {
            pairPath = `${ExchangeRouteBasePath[EExchange.dex]}/${firstPair.id}`;
          }
        }
      }

      redirect({ projectId: newProjects[0]?.id ?? undefined, pairPath });

      return thunkApi.fulfillWithValue({
        projects: newProjects,
        archivedProjects: newArchivedProjects,
      });
    }

    return thunkApi.rejectWithValue(errorMessage);
  } catch (error) {
    console.log(error);
  }
});

const projectsSlice = createSlice({
  name: 'projects',
  initialState: userSliceInitialState as IProjectsSliceState,
  extraReducers: builder => {
    builder.addCase(
      getAllProjectsAndPairs.fulfilled,
      (store, { payload }: { payload: { projects: IProject[]; archivedProjects: IProject[] } }) => {
        store.projects = payload.projects;
        store.archivedProjects = payload.archivedProjects;
      },
    );
    builder.addCase(getAllProjectsAndPairs.rejected, (_store, { payload }) => {
      console.error('Failed to load projects', payload);
    });
  },
  reducers: {
    addProject: (store, { payload }: { payload: IProject }) => {
      const newProjects = store.projects ? [...store.projects] : [];
      newProjects.push(payload);

      store.projects = newProjects;
    },
    deleteProject: (store, { payload }: { payload: number }) => {
      const isArchived = isProjectArchived({ active: store.projects, projectId: payload });
      const field = isArchived ? 'archivedProjects' : 'projects';

      const newProjects = store[field]
        ? [...store[field]].filter(project => project.id !== payload)
        : [];

      store[field] = newProjects;
    },
    archiveProject: (store, { payload }: { payload: number }) => {
      const find = store.projects?.find(el => el.id === payload);

      const newProjects = store.projects
        ? [...store.projects].filter(project => project.id !== payload)
        : [];

      store.projects = newProjects;

      if (find) {
        store.archivedProjects = store.archivedProjects
          ? store.archivedProjects.concat([find])
          : [find];
      }
    },
    updateProject: (
      store,
      { payload }: { payload: { id: number; newProjectName: string; newProjectNotes: string } },
    ) => {
      const isArchived = isProjectArchived({ active: store.projects, projectId: payload.id });
      const field = isArchived ? 'archivedProjects' : 'projects';

      const newProjects = store[field]
        ? [...store[field]].map(project => {
            if (project.id === payload.id) {
              return { ...project, notes: payload.newProjectNotes, name: payload.newProjectName };
            }
            return project;
          })
        : [];

      store[field] = newProjects;
    },
    addNewPairToProject: (
      store,
      { payload }: { payload: { pair: IDexPairShort | ICexPairShort; type: EExchange } },
    ) => {
      const isArchived = isProjectArchived({
        active: store.projects,
        projectId: payload.pair.project_id,
      });
      const field = isArchived ? 'archivedProjects' : 'projects';

      if (!store[field]) return;

      const newProjects = [...store[field]].map(project => {
        if (project.id === payload.pair.project_id) {
          const newProject = { ...project };

          if (payload.type === EExchange.dex) {
            newProject.dexPairs.push(payload.pair as IDexPairShort);
          }

          if (payload.type === EExchange.cex) {
            newProject.cexPairs.push(payload.pair as ICexPairShort);
          }

          return newProject;
        }
        return project;
      });
      store[field] = newProjects;
    },
    updatePairInProject: (
      store,
      {
        payload,
      }: { payload: { pairId: number; type: EExchange; projectId: number; notes: string } },
    ) => {
      const isArchived = isProjectArchived({
        active: store.projects,
        projectId: payload.projectId,
      });
      const field = isArchived ? 'archivedProjects' : 'projects';

      if (!store[field]) return;

      const newProjects = [...store[field]].map(project => {
        if (project.id === payload.projectId) {
          const newProject = { ...project };

          if (payload.type === EExchange.dex) {
            newProject.dexPairs = [...newProject.dexPairs].map(pair => {
              if (pair.id === payload.pairId) {
                return { ...pair, notes: payload.notes };
              }

              return pair;
            });
          }

          if (payload.type === EExchange.cex) {
            newProject.cexPairs = [...newProject.cexPairs].map(pair => {
              if (pair.id === payload.pairId) {
                return { ...pair, notes: payload.notes };
              }

              return pair;
            });
          }

          return newProject;
        }
        return project;
      });
      store[field] = newProjects;
    },
    deletePairFromProject: (
      store,
      { payload }: { payload: { pairId: number; type: EExchange; projectId: number } },
    ) => {
      const isArchived = isProjectArchived({
        active: store.projects,
        projectId: payload.projectId,
      });
      const field = isArchived ? 'archivedProjects' : 'projects';

      if (!store[field]) return;

      const newProjects = [...store[field]].map(project => {
        if (project.id === payload.projectId) {
          const newProject = { ...project };

          if (payload.type === EExchange.dex) {
            newProject.dexPairs = [...newProject.dexPairs].filter(
              pair => pair.id !== payload.pairId,
            );
          }

          if (payload.type === EExchange.cex) {
            newProject.cexPairs = [...newProject.cexPairs].filter(
              pair => pair.id !== payload.pairId,
            );
          }

          return newProject;
        }
        return project;
      });
      store[field] = newProjects;
    },
    setSelectedProjectInPage: (store, { payload }: { payload: IGetByIdProject | null }) => {
      store.selectedProjectInPage = payload;
    },
    /**
     * Set projects from cache (IndexedDB hydration)
     * Used by the new caching layer to sync cached data to Redux
     */
    setProjectsFromCache: (
      store,
      { payload }: { payload: { projects: IProject[]; archivedProjects: IProject[] } },
    ) => {
      store.projects = payload.projects;
      store.archivedProjects = payload.archivedProjects;
    },
  },
});

export { getAllProjectsAndPairs };

export const {
  addProject,
  deleteProject,
  archiveProject,
  updateProject,
  addNewPairToProject,
  updatePairInProject,
  deletePairFromProject,
  setSelectedProjectInPage,
  setProjectsFromCache,
} = projectsSlice.actions;

export default projectsSlice.reducer;
